/**
 * TanStack React Query key factory i hook-ovi za notice API.
 * staleTime 60s — admin, početna i javne stranice dele keš.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import NoticeService from '../../../shared/services/NoticeService';
import {
  enrichNoticesWithPublicSlugs,
  getNoticeSlugFromRecord,
  parseNoticeSearchOneResponse,
} from '../../../shared/utils/noticeSlug';

/** Query key factory za notice keš. */
export const noticeKeys = {
  all: ['notices'],
  list: (filters = {}) => [...noticeKeys.all, 'list', filters],
  archived: () => [...noticeKeys.all, 'archived'],
  detail: (slug) => [...noticeKeys.all, 'detail', slug],
  /** @deprecated Koristiti noticeKeys.list() */
  search: () => noticeKeys.list({}),
};

const STALE_TIME_MS = 60 * 1000;

/**
 * Parsira listu objava iz API odgovora.
 * @param {object|object[]} data - Sirovi API odgovor.
 * @returns {object[]} Niz objava.
 */
function parseNoticesListPayload(data) {
  return Array.isArray(data) ? data : (data?.notices || data?.data || []);
}

/**
 * Fetch aktivnih objava obogaćenih javnim slug-ovima.
 * @param {AbortSignal} signal - Signal za otkazivanje zahteva.
 * @returns {Promise<object[]>}
 */
async function fetchActiveNoticesWithSlugs(signal) {
  const data = await NoticeService.search(signal);
  return enrichNoticesWithPublicSlugs(parseNoticesListPayload(data), signal);
}

/**
 * Fetch arhiviranih objava obogaćenih javnim slug-ovima.
 * @param {AbortSignal} signal - Signal za otkazivanje zahteva.
 * @returns {Promise<object[]>}
 */
async function fetchArchivedNoticesWithSlugs(signal) {
  const data = await NoticeService.searchArchived(signal);
  return enrichNoticesWithPublicSlugs(parseNoticesListPayload(data), signal);
}

/**
 * Fetch jedne objave po slug-u preko notice/search-one (javni endpoint).
 *
 * @param {string} slug - Javni slug objave.
 * @param {AbortSignal} signal - Signal za otkazivanje zahteva.
 * @returns {Promise<object|null>} Objava sa rezolvovanim slug-om ili `null`.
 */
async function fetchNoticeBySlug(slug, signal) {
  if (!slug) return null;
  const raw = await NoticeService.searchOne(slug, signal);
  const notice = parseNoticeSearchOneResponse(raw);
  if (!notice) return null;
  const resolvedSlug = getNoticeSlugFromRecord(notice) || slug;
  return { ...notice, slug: resolvedSlug };
}

/**
 * React Query hook za listu aktivnih objava.
 * Filtriranje po tipu/vidljivosti se radi u potrošaču.
 *
 * @param {object} [filters] - Filteri (deo queryKey-a; filtriranje u potrošaču).
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult<object[]>}
 */
export function useNotices(filters = {}, options = {}) {
  return useQuery({
    queryKey: noticeKeys.list(filters),
    queryFn: ({ signal }) => fetchActiveNoticesWithSlugs(signal),
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * React Query hook za jednu objavu po slug-u.
 *
 * @param {string} slug - Javni slug objave.
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult<object|null>}
 */
export function useNotice(slug, options = {}) {
  return useQuery({
    queryKey: noticeKeys.detail(slug),
    queryFn: ({ signal }) => fetchNoticeBySlug(slug, signal),
    enabled: !!slug,
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * Alias za useNotices({}) — zadržan radi backward kompatibilnosti.
 *
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult<object[]>}
 */
export function useNoticeSearchQuery(options = {}) {
  return useNotices({}, options);
}

/**
 * React Query hook za arhivirane objave (POST /notice/search-archived).
 *
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult<object[]>}
 */
export function useNoticeArchivedSearchQuery(options = {}) {
  return useQuery({
    queryKey: noticeKeys.archived(),
    queryFn: ({ signal }) => fetchArchivedNoticesWithSlugs(signal),
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * Alias za useNotice(slug) — zadržan radi backward kompatibilnosti.
 *
 * @param {string} slug - Javni slug objave.
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult<object|null>}
 */
export function useNoticeDetailQuery(slug, options = {}) {
  return useNotice(slug, options);
}

/**
 * Vraća QueryClient instancu (helper za cache operacije u notice modulu).
 *
 * @returns {import('@tanstack/react-query').QueryClient}
 */
export function useNoticeQueryClient() {
  return useQueryClient();
}
